// src/constants/Content.js

import Logo from '../assets/images/logo.svg';
import FooterLogo from '../assets/images/logo-dark.svg';

/**
 * Currency configuration by region
 * Used to dynamically display currency symbols and amounts
 */
export const currencyConfig = {
  UK: { symbol: '£', lossAmount: '2,000', budgetAmount: '15,000' },
  US: { symbol: '$', lossAmount: '2,500', budgetAmount: '18,000' },
  EU: { symbol: '€', lossAmount: '2,000', budgetAmount: '15,000' },
};

/**
 * Replaces currency placeholders in text based on detected region
 * Placeholders: {{CURRENCY}}, {{LOSS_AMOUNT}}, {{BUDGET_AMOUNT}}
 *
 * @param {string} text - Text containing placeholders
 * @param {string} region - Detected region (UK, US, EU)
 * @returns {string} Text with placeholders replaced
 */
export function localizeCurrency(text, region = 'EU') {
  const config = currencyConfig[region] || currencyConfig.EU;
  return text
    .replace(/\{\{CURRENCY\}\}/g, config.symbol)
    .replace(/\{\{LOSS_AMOUNT\}\}/g, `${config.symbol}${config.lossAmount}`)
    .replace(/\{\{BUDGET_AMOUNT\}\}/g, `${config.symbol}${config.budgetAmount}`);
}

/**
 * Landing page variant overrides for Google Ads campaigns.
 * Each variant overrides heroSection, featuresBar, and SEO for message-match.
 * Used via ?v= query parameter (e.g. ?v=car-finder).
 */

/**
 * Post-onboarding signup-gate content (V5 "control room" variant).
 *
 * Stats are seeded from a one-shot prod query (2026-04-28). Phase 2 wires
 * them to a /get-gate-stats edge function — until then `isLiveSnapshot`
 * stays false and the gate renders the constants below.
 *
 * Provenance:
 *   - listings_scored_today: total UK active inventory we scan daily, from a
 *     Marketcheck /search/car/uk/active probe (rows=0 -> num_found = 535,075
 *     on 2026-06-11). Displayed as "535,000 UK listings scanned daily" — the
 *     live pool rounded to a clean figure. NOTE the field name is legacy; it
 *     now represents the scanned pool, not feed surfacings.
 *   - price_drops_7d: PLACEHOLDER (827). The price_change_* columns on
 *     user_listing_feeds exist but aren't populated yet — replace with the
 *     real count once the tracking pipeline ships.
 *   - median_below_market_gbp: median of (cached market price − asking
 *     price) across the 61/126 surfaced entries currently below market.
 *     Source: SQL on user_listing_feeds × marketcheck_listing_cache.
 *   - users_total: 1,304 in auth.users — display as "1,300+".
 *
 * The testimonial is a placeholder until we have a real customer quote
 * with opt-in (isMock=true gates that on the consumer side).
 */
export const signupGateMocks = {
  testimonial: {
    quote:
      "Doocars pinged me when a Jimny dropped, £1,200 under market, listed 90 days, nobody spotted it.",
    name: 'Maria',
    age: 26,
    purchase: 'bought a Suzuki Jimny',
    avatarSrc: require('../../assets/images/landing/optimized/testimonial_avatar_1.webp'),
    avatarHue: 220, // fallback hue for the initials chip if avatarSrc fails to load
    isMock: true,
  },
  stats: {
    listings_scored_today: 535000,
    price_drops_7d: 827, // placeholder — wire to real tracking in Phase 2
    median_below_market_gbp: 3500, // prod-derived median saving on tracked listings
    users_total: 1300,
    isLiveSnapshot: false, // flips true when /get-gate-stats lands
  },
};

export const landingPageVariants = {
  'car-finder': {
    onboardingIntent: 'car-finder',
    seo: {
      title: 'Doocars - Not Sure Which Car? We\u2019ll Match You',
      description:
        'Not sure which car fits your life? Answer 20 questions. We match you to 5 cars, then scan every UK listing and score each one daily.',
    },
    heroSection: {
      titleLine1: 'Not Sure Which Car Fits Your Life?',
      titleLine2: "We'll help you figure it out.",
      subtitle:
        'Wrong car = wrong insurance, wrong running costs, years of regret.\nAnswer 20 questions \u2014 we match you to 5 cars, then scan every UK listing daily.',
      heroImage: {
        small: require('../../assets/images/landing/responsive/hero/small.webp'),
        small2x: require('../../assets/images/landing/responsive/hero/small-2x.webp'),
        medium: require('../../assets/images/landing/responsive/hero/medium.webp'),
        medium2x: require('../../assets/images/landing/responsive/hero/medium-2x.webp'),
        large: require('../../assets/images/landing/responsive/hero/large.webp'),
        large2x: require('../../assets/images/landing/responsive/hero/large-2x.webp'),
      },
    },
  },
  'car-finder-gdn': {
    onboardingIntent: 'car-finder',
    seo: {
      title: 'Doocars - Not Sure Which Car? We\u2019ll Match You',
      description:
        'Not sure which car fits your life? Answer 20 questions. We match you to 5 cars, then scan every UK listing and score each one daily.',
    },
    heroSection: {
      titleLine1: 'Buying Your First Car?',
      titleLine2: "We'll find the right one for you.",
      subtitle:
        'Wrong car = wrong insurance, wrong running costs. Take a quick quiz, get matched to 5 cars, scored daily across every UK listing.',
      heroImage: {
        small: require('../../assets/images/landing/responsive/hero/small.webp'),
        small2x: require('../../assets/images/landing/responsive/hero/small-2x.webp'),
        medium: require('../../assets/images/landing/responsive/hero/medium.webp'),
        medium2x: require('../../assets/images/landing/responsive/hero/medium-2x.webp'),
        large: require('../../assets/images/landing/responsive/hero/large.webp'),
        large2x: require('../../assets/images/landing/responsive/hero/large-2x.webp'),
      },
    },
  },
  deals: {
    onboardingIntent: 'deals',
    seo: {
      title: 'Doocars - Every UK Used Car Listing, Scored Daily',
      description:
        'Every major UK car site, scanned daily. Each listing scored against 10 factors and your preferences. Distance, mileage, history, all tracked for you.',
    },
    heroSection: {
      titleLine1: 'Let AI Do the Car Hunting',
      titleLine2: 'Best matches in your inbox. Every morning.',
      subtitle:
        'Pick your models. We scan every UK car site, score each listing, and send you only what\'s worth seeing.',
      featuresBar: {
        features: [
          { icon: 'scan-daily', text: 'All Sites Scanned Daily' },
          { icon: 'match-score', text: 'Match Scoring 0\u2013100%' },
          { icon: 'price-intel', text: 'Price vs Market' },
          { icon: 'daily-alerts', text: 'Price Drop Alerts' },
        ],
      },
    },
  },
  'price-check': {
    onboardingIntent: 'deals',
    seo: {
      title: 'Doocars - Used Car Price Checker & Market Scanner',
      description:
        'Every listing checked against the UK market and your preferences: price, mileage, days listed, distance. Scored 0-100% so you know before you go.',
    },
    heroSection: {
      titleLine1: 'Is That Car Actually Worth It?',
      titleLine2: 'We check every listing against the UK market. Daily.',
      subtitle:
        'Pick your target. We score it on 9 factors, track the price, and alert you when it drops below market.',
      featuresBar: {
        features: [
          { icon: 'scan-daily', text: 'Market Scanned Daily' },
          { icon: 'match-score', text: 'Every Factor Scored' },
          { icon: 'price-intel', text: 'Price vs Market' },
          { icon: 'daily-alerts', text: 'Price Drop Alerts' },
        ],
      },
    },
  },
  'running-costs': {
    onboardingIntent: 'deals',
    seo: {
      title: 'Doocars - Used Car Running Cost Checker for UK Listings',
      description:
        'Every UK listing scored on running costs: fuel, road tax, insurance group, and maintenance forecast, alongside price vs market and service history. Free.',
    },
    heroSection: {
      titleLine1: 'What Will It Cost to Run?',
      titleLine2: 'We score running costs on every UK listing. Daily.',
      subtitle:
        'Pick your car. We score maintenance costs, fuel, insurance group and road tax, alongside price vs market and service history. Free.',
      featuresBar: {
        features: [
          { icon: 'match-score', text: 'Running Costs Scored' },
          { icon: 'scan-daily', text: 'Maintenance Forecast' },
          { icon: 'price-intel', text: 'Price vs Market' },
          { icon: 'daily-alerts', text: 'Daily Alerts' },
        ],
      },
    },
  },
};

export const landingPageContent = {
  seo: {
    title: 'Doocars - Every UK Car Listing, Scored for You Daily',
    description:
      'Every UK used car listing, scored 0-100% for you. Price, distance, mileage, history, all checked daily across every major site. 3 min setup.',
    og: {
      title: 'Every UK Car Listing - Scored Daily',
      description:
        'We scan every major UK dealer and marketplace daily. Each listing scored 0-100%: price, distance, mileage, history. All tracked for you.',
      type: 'website',
      url: 'https://www.doocars.com/',
      image: 'https://www.doocars.com/og-image.png',
    },
    twitter: {
      card: 'summary_large_image',
      title: 'Every UK Car Listing - Scored Daily',
      description:
        'Every UK car listing scored 0-100% against what you need. Price drops, distance, mileage, history, checked daily across all major sites.',
      image: 'https://www.doocars.com/twitter-image.png',
    },
  },
  header: {
    logo: Logo,
    navLinks: [
      { title: 'About', target: '#about' },
      { title: 'Sources', target: '#sources' },
      { title: 'How it works', target: '#how-it-works' },
      { title: 'Testimonials', target: '#testimonials' },
      { title: 'Pricing', target: '/pricing', requiresPaywall: true },
      { title: 'FAQ', target: '#faq' },
    ],
    cta: {
      title: 'Find my car',
    },
  },
  heroSection: {
    titleLine1: 'Every UK Car Listing - Scored Daily',
    titleLine2: "You just see what's worth your time.",
    subtitle:
      'Tell us what you need. We build your profile, scan every listing, and score\neach one, so you never waste time on the wrong car again.',
    cta: {
      title: 'Find my Car',
    },
    featuresBar: {
      features: [
        { icon: 'scan-daily', text: 'Daily Market Scan' },
        { icon: 'match-score', text: 'Match Scoring 0–100%' },
        { icon: 'price-intel', text: 'Price Intelligence' },
        { icon: 'daily-alerts', text: 'Daily Alerts' },
      ],
    },
    heroCards: [
      {
        image: {
          small: require('../../assets/images/landing/responsive/hero-card/small.webp'),
          small2x: require('../../assets/images/landing/responsive/hero-card/small-2x.webp'),
          medium: require('../../assets/images/landing/responsive/hero-card/medium.webp'),
          medium2x: require('../../assets/images/landing/responsive/hero-card/medium-2x.webp'),
        },
        badge: 'NEW',
        title: '2017 BMW 1 Series 118i SE Auto',
        specs: ['Automatic', '56,100 m', 'London, 2KM away'],
        matchSnippet: '£1,501 below market, low mileage for year, close to you',
        matchPercent: 91,
        price: '£10,499',
      },
    ],
  },
  // Skeletons for other sections will be added in subsequent tasks
  introductorySection: {
    headline: "Every listing gets a score.\nHere's what's behind it.",
    subheadline:
      'Not a gut feeling. Ten real factors, calculated daily across the whole UK market.',
    scoreBreakdown: [
      { value: '£1,501 below market average', label: 'Price vs Market', score: 81, tier: 'green' },
      { value: '6 miles away', label: 'Distance to seller', score: 64, tier: 'green' },
      {
        value: '79,100 miles',
        label: '7,422 above average for this model',
        score: 42,
        tier: 'orange',
      },
      { value: 'Well Equipped', label: 'Trim level', score: 75, tier: 'green' },
      {
        value: 'Listed 111 Days ago',
        label: '74d market avg, likely negotiable',
        score: 85,
        tier: 'green',
      },
      { value: '2017', label: 'Within your preferred year range', score: 64, tier: 'green' },
      { value: '£377 dropped', label: 'Initial price was £10,876', score: 80, tier: 'green' },
      { value: 'Automatic', label: 'Transmission type', score: 100, tier: 'green' },
      { value: '5 Previous Owners', label: 'Previous owner history', score: 10, tier: 'red' },
      {
        value: 'ULEZ Compliant',
        label: 'Low emission zone compliance',
        score: 100,
        tier: 'green',
      },
    ],
  },
  researchSection: {
    headline: 'We do the work.\nYou pick the car.',
    body: 'Hundreds of listings. No way to know if the price is fair, the mileage is reasonable, or why a car has been sitting unsold for three months.\n\nMost buyers spend weeks digging through noise, or they settle, and hope for the best.',
    cards: [
      {
        title: 'Every listing source in the UK, scanned daily',
        description:
          "Dealers, classifieds, auction houses, trade platforms, we cover them all so you don't have to.",
        illustration: 'scan',
      },
      {
        title: 'Only the best, filtered for you',
        description:
          'No noise. Every listing you see matches your criteria, price, mileage, condition.',
        illustration: 'filter',
      },
      {
        title: 'Daily listing in your inbox',
        description:
          'Best matches delivered every morning. Never miss a deal because you forgot to check.',
        illustration: 'inbox',
      },
    ],
  },
  getMoreSection: {
    headline: 'Get More Than Just Car Names',
    subheadline:
      'Each recommendation comes with a comprehensive analysis \ncovering everything that matters for your decision',
    tags: [
      'Budget Match Verified',
      'Fuel Economy Reality',
      'Lifestyle Compatibility',
      'Driving Habits',
      'Priorities',
      'Running Costs & Your Priorities',
      'Engine Options',
      'Towing & Hauling Capabilities',
      'Off-Road Prowess',
      'Cargo & Storage',
      'Infotainment & Connectivity',
      'Cabin Space & Passenger Comfort',
      'Driver Assistance Systems',
      'General Reliability Reputation',
      'Repair Costs',
      'Maintenance Frequency',
      'City Driving Suitability',
      'Hauling Capacity',
      'Connectivity Options',
      'Repair Costs',
    ],
  },
  testimonialsSection: {
    headline: "People stopped scrolling.\nHere's why.",
    subheadline: 'Real buyers who let Doocars watch the market for them',
    testimonials: [
      {
        quote:
          "Used to check AutoTrader every morning before work, bit obsessive honestly. Now Doocars just pings me when something decent comes up. Found my Jimny £1,200 under market, been listed 90 days and nobody spotted it.",
        author: {
          name: 'Maria, 26',
          image: require('../../assets/images/landing/optimized/testimonial_avatar_1.webp'),
        },
        carImage: {
          small: require('../../assets/images/landing/responsive/testimonial-1/small.webp'),
          small2x: require('../../assets/images/landing/responsive/testimonial-1/small-2x.webp'),
          medium: require('../../assets/images/landing/responsive/testimonial-1/medium.webp'),
          medium2x: require('../../assets/images/landing/responsive/testimonial-1/medium-2x.webp'),
          large: require('../../assets/images/landing/responsive/testimonial-1/large.webp'),
          large2x: require('../../assets/images/landing/responsive/testimonial-1/large-2x.webp'),
        },
        carImageWidths: {
          small: 382,
          small2x: 764,
          medium: 669,
          medium2x: 1338,
          large: 791,
          large2x: 1582,
        },
      },
      {
        quote:
          "Was comparing RAV4s across three different sites, couldn't tell if any of the prices were actually decent. Doocars scored them all for me, distance, mileage, price vs market. Went for one rated 87% and it's been spot on.",
        author: {
          name: 'Thomas, 29',
          image: require('../../assets/images/landing/optimized/testimonial_avatar_2.webp'),
        },
        carImage: {
          small: require('../../assets/images/landing/responsive/testimonial-2/small.webp'),
          small2x: require('../../assets/images/landing/responsive/testimonial-2/small-2x.webp'),
          medium: require('../../assets/images/landing/responsive/testimonial-2/medium.webp'),
          medium2x: require('../../assets/images/landing/responsive/testimonial-2/medium-2x.webp'),
          large: require('../../assets/images/landing/responsive/testimonial-2/large.webp'),
          large2x: require('../../assets/images/landing/responsive/testimonial-2/large-2x.webp'),
        },
        carImageWidths: {
          small: 382,
          small2x: 764,
          medium: 669,
          medium2x: 1338,
          large: 791,
          large2x: 1582,
        },
      },
      {
        quote:
          "Added a Golf, Focus, and Civic to my tracker just to see what would happen. Week later got an alert for a Focus with a £400 price drop, 12 miles from me, 91% match. Didn't scroll through a single listing.",
        author: {
          name: 'Sophie, 24',
          image: require('../../assets/images/landing/optimized/testimonial_avatar_3.webp'),
        },
        carImage: {
          small: require('../../assets/images/landing/responsive/testimonial-3/small.webp'),
          small2x: require('../../assets/images/landing/responsive/testimonial-3/small-2x.webp'),
          medium: require('../../assets/images/landing/responsive/testimonial-3/medium.webp'),
          medium2x: require('../../assets/images/landing/responsive/testimonial-3/medium-2x.webp'),
          large: require('../../assets/images/landing/responsive/testimonial-3/large.webp'),
          large2x: require('../../assets/images/landing/responsive/testimonial-3/large-2x.webp'),
        },
        carImageWidths: {
          small: 382,
          small2x: 764,
          medium: 669,
          medium2x: 1338,
          large: 791,
          large2x: 1582,
        },
      },
    ],
  },
  howItWorksSection: {
    mainHeadline: 'So ... how does it work?',
    steps: [
      {
        title: 'Build Your Profile',
        description:
          'Answer 20 quick questions about what you need. Budget, lifestyle, priorities. We turn that into a precise brief.',
        image: require('../assets/images/optimized/how-it-works-1.webp'),
      },
      {
        title: 'We Scan the Market',
        description:
          'Every day, Doocars scans UK car listings from across the web. Not once. Every day.',
        image: require('../assets/images/optimized/how-it-works-2.webp'),
      },
      {
        title: 'Get Your Matches',
        description:
          'We surface only the listings that fit your brief, each one scored, ranked, and explained.',
        listings: [
          {
            badge: 'NEW',
            title: '2017 BMW 1 Series 118i SE Auto',
            price: '£10,499',
            specs: 'Auto · 56,100 mi · London, 2mi',
            matchPercent: 91,
            highlight: 'Price below market · No prev. owners',
          },
          {
            title: '2018 BMW 1 Series 120i M Sport Auto',
            price: '£12,950',
            specs: 'Auto · 42,300 mi · Reading, 40mi',
            matchPercent: 84,
            highlight: 'Low mileage for year',
          },
          {
            title: '2017 BMW 1 Series 116d Sport',
            price: '£9,200',
            specs: 'Manual · 98,400 mi · Brighton, 53mi',
            matchPercent: 76,
            highlight: 'Price dropped £520',
          },
          {
            title: '2017 BMW 1 Series 118d SE Auto',
            price: '£10,800',
            specs: 'Auto · 71,800 mi · Watford, 18mi',
            matchPercent: 79,
            highlight: 'Good diesel economy',
          },
          {
            title: '2019 BMW 1 Series 118i Sport Auto',
            price: '£13,490',
            specs: 'Auto · 34,600 mi · Cambridge, 62mi',
            matchPercent: 88,
            highlight: 'Very low mileage',
          },
        ],
      },
    ],
  },
  faqSection: {
    headline: "Questions? We've Got Answers",
    subheadline: 'Everything you need to know about how\nDoocars works',
    questions: [
      {
        question: 'Where do the listings come from?',
        answer:
          'We scan the entire UK used car market daily: AutoTrader, Gumtree, Motors.co.uk, and more. Every listing is scored against your personal preferences so you only see what actually fits.',
      },
      {
        question: 'How does the scoring work?',
        answer:
          'Each listing is scored 0-100% based on six factors: price vs market average, distance to you, mileage, year, fuel type match, and price history. You can see exactly how every score breaks down. No black boxes.',
      },
      {
        question: 'Is Doocars free?',
        answer:
          'Getting started is completely free. Set up your profile, track up to 5 car models, and get your first daily matches. We plan to offer a premium tier in the future with more models, faster alerts, and extra features.',
      },
      {
        question: 'Do you work with dealers?',
        answer:
          "No. Doocars is built entirely for buyers. We don't take dealer money, promote paid placements, or rank listings based on anything other than how well they match you.",
      },
      {
        question: 'How often is the market scanned?',
        answer:
          'Every day. New listings, price drops, and long-listed deals are flagged automatically. You get alerts via push notification or email, no need to keep checking manually.',
      },
      {
        question: 'Can I track more than one car model?',
        answer:
          'Yes. You can track 1-5 models at the same time, for example a Skoda Karoq, Toyota C-HR, and Nissan Qashqai. Each model gets its own tab with scored listings.',
      },
      {
        question: 'Do you filter out risky listings?',
        answer:
          'Yes. Salvaged, written-off, and category-flagged vehicles are automatically excluded from your results. You only see listings worth your time.',
      },
      {
        question: 'What area does Doocars cover?',
        answer:
          'The entire UK market. Listings are scored by distance to your location, so nearby cars rank higher, but you can see everything nationwide if you want to.',
      },
    ],
  },
  finalCTA: {
    headline: 'Stop scrolling. Start knowing.',
    subheadline: "Answer 20 questions. We'll handle the rest, every single day.",
    buttonText: 'Find my Car',
    timeText: 'It takes just 3 min',
    image: require('../../assets/images/landing/optimized/final-cta-car.webp'),
  },
  // Step 5: shared 3-SKU pricing structure consumed by both the standalone
  // /pricing page (PricingSection) and the in-app upgrade modal (UpgradeModal).
  // Single source of truth so the marketing surface and conversion surface stay coherent.
  pricing: {
    headline: 'Smart car buying.\nChoose your plan.',
    subheadline:
      'No dealer bias. No promoted listings. Just the intelligence you need to buy the right car.',
    // V1 redesign — the three plans share the same toolkit; only the billing
    // changes. The redesigned card carries one feature list on the left and
    // three "PlanTile" rows on the right, each with its own CTA. Copy below
    // drives the dark navy panel headline + intro.
    secondaryHeadline: "What's included",
    secondarySubheadline:
      'Every plan unlocks all features.\nChoose what fits your timeline.',
    unifiedFeatures: [
      { title: 'Fresh matches in your inbox, daily' },
      { title: 'Price drop alerts the moment they happen' },
      { title: 'MOT & DVLA data for each listing' },
      { title: 'Overpriced car listings never reach you' },
      { title: 'Average savings: £3,500 below market' },
      { title: 'Only see cars that fit your needs' },
      { title: 'True cost of ownership calculated' },
      { title: 'Any AutoTrader listing analysed instantly' },
      { title: 'Fair price data for negotiation' },
      { title: 'Side-by-side car comparisons' },
      { title: 'Negotiate anonymously with dealers', note: 'Beta' },
    ],
    trustLine: 'Secure checkout by Paddle.',
    tryFreeLabel: 'Try free first. No card required.',
    packages: [
      {
        id: 'monthly',
        name: 'Monthly',
        subtitle: 'Flexible, cancel any time',
        // V1 PlanTile copy — `cadence` is the suffix after the price,
        // `renewalNote` sets billing expectations when the headline price
        // reflects an intro discount.
        cadence: '/ month',
        cadenceWithDiscount: '/ 1st month',
        renewalNote: 'then {{fullPrice}}/mo',
        savingsCopy: '50% off first month',
        featuresIntro: "Same features as Car Buyer's Pass:",
        features: [
          {
            title: 'AI negotiator (Early access)',
            description:
              'Our AI contacts dealers anonymously using MOT history, market pricing, and days-on-market data to negotiate a better price. You approve every message before it sends. Early access, available to all Pro subscribers.',
          },
          {
            title: '10-factor scoring',
            description:
              'Price vs market (from UK dealer data feeds), mileage vs average, MOT history (DVSA), running costs, depreciation, days on market, trim, distance, transmission, owner count, all matched to your quiz answers.',
          },
          {
            title: 'Running costs + alerts',
            description:
              '3-year running costs forecast on every listing plus real-time alerts when prices drop on cars you\'re tracking.',
          },
          'Paste-a-link, compare, PDF reports',
        ],
        footnote: "Best for: buyers who aren't sure how long they'll need it. Switch to the Pass anytime.",
      },
      {
        id: 'car_buyers_pass',
        name: '90 Days',
        subtitle: 'One payment. 90 days. No subscription to cancel.',
        cadence: '/ 3 months',
        savingsCopy: '{{perMonth}}/mo, save {{pct}}% vs monthly',
        isHighlighted: true,
        badge: 'One-time payment',
        featuresIntro: 'Everything you need to buy:',
        features: [
          {
            title: 'AI negotiator (Early access)',
            description:
              'Our AI contacts dealers anonymously using MOT history, market pricing, and days-on-market data to negotiate a better price. You approve every message before it sends. Early access, available to all Pro subscribers.',
          },
          {
            title: '10-factor score on every listing',
            description:
              'Price vs market (from UK dealer data feeds), mileage vs average, MOT history (DVSA), running costs, depreciation, days on market, trim, distance, transmission, owner count, all matched to your quiz answers.',
          },
          {
            title: '3-year running costs forecast',
            description:
              'Year-by-year maintenance breakdown: service, tyres, brakes, repairs. From industry maintenance records. See what the car will really cost before you buy.',
          },
          {
            title: 'All your models tracked daily',
            description:
              "Add every model you're considering. We scan the UK market overnight and score new matches against your preferences every morning.",
          },
          'Real-time price drop alerts',
          'Paste any listing URL to score it',
          'Comparison mode (2 cars side by side)',
          'PDF vehicle reports',
        ],
        footnote: 'Best for: active car buyers who want one payment covering their full buying window.',
      },
      {
        id: 'annual',
        name: 'Annual',
        subtitle: 'Best per-month value. Locked-in rate.',
        cadence: '/ year',
        savingsCopy: '{{perMonth}}/mo, save {{pct}}% vs monthly',
        featuresIntro: 'Everything in Monthly, plus:',
        features: [
          '50% savings vs monthly (save {{savings}}/yr)',
          'Price locked for 12 months',
          'Priority access to new features',
          'Best fit if your search takes longer than a few weeks',
        ],
        footnote: 'Best for: buyers helping family or friends, or planning multiple purchases.',
      },
    ],
    foundersRateLabel: "Founder's Rate",
    standardRenewalLabel: 'then £{price} / {cycle} after',
  },
  footer: {
    logo: FooterLogo,
    logoLight: Logo,
    links: [
      { title: 'Terms of Service', target: '/terms-and-conditions' },
      { title: 'Privacy Policy', target: '/privacy-policy' },
      { title: 'Contact us' },
    ],
  },
};
